import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Shield,
  DollarSign,
  Heart,
  Bot,
  Sparkles,
  Search,
  Scale,
  FileText,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Edit,
  Loader2
} from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);
};

const PROVIDERS = [
  "Northwestern Mutual",
  "New York Life",
  "MassMutual",
  "Prudential",
  "MetLife",
  "State Farm",
  "Nationwide",
  "Lincoln Financial",
  "Principal",
  "Other"
];

export default function Insurance() {
  const { api } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  
  // AI Search Bot state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [searchForm, setSearchForm] = useState({
    search_type: "any",
    coverage_amount: "",
    monthly_budget: "",
    age: "",
    health_status: "good",
    smoker: false,
    term_length: "",
    additional_requirements: ""
  });
  
  const [formData, setFormData] = useState({
    policy_number: "",
    provider: "",
    premium_amount: "",
    payment_frequency: "",
    death_benefit: "",
    cash_value: "",
    start_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const res = await api.get("/insurance");
      setPolicies(res.data);
    } catch (error) {
      toast.error("Failed to fetch policies");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/insurance", {
        ...formData,
        premium_amount: parseFloat(formData.premium_amount),
        death_benefit: parseFloat(formData.death_benefit),
        cash_value: parseFloat(formData.cash_value || 0)
      });
      toast.success("Policy added");
      setDialogOpen(false);
      setFormData({
        policy_number: "",
        provider: "",
        premium_amount: "",
        payment_frequency: "",
        death_benefit: "",
        cash_value: "",
        start_date: new Date().toISOString().split('T')[0]
      });
      fetchPolicies();
    } catch (error) {
      toast.error("Failed to add policy");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      policy_number: policy.policy_number || "",
      provider: policy.provider || "",
      premium_amount: policy.premium_amount?.toString() || "",
      payment_frequency: policy.payment_frequency || "",
      death_benefit: policy.death_benefit?.toString() || "",
      cash_value: policy.cash_value?.toString() || "",
      start_date: policy.start_date || new Date().toISOString().split('T')[0]
    });
    setEditDialogOpen(true);
  };

  const handleUpdatePolicy = async (e) => {
    e.preventDefault();
    if (submitting || !editingPolicy) return;
    setSubmitting(true);
    try {
      await api.put(`/insurance/${editingPolicy.id}`, {
        ...formData,
        premium_amount: parseFloat(formData.premium_amount),
        death_benefit: parseFloat(formData.death_benefit),
        cash_value: parseFloat(formData.cash_value || 0)
      });
      toast.success("Policy updated");
      setEditDialogOpen(false);
      setEditingPolicy(null);
      setFormData({
        policy_number: "",
        provider: "",
        premium_amount: "",
        payment_frequency: "",
        death_benefit: "",
        cash_value: "",
        start_date: new Date().toISOString().split('T')[0]
      });
      fetchPolicies();
    } catch (error) {
      toast.error("Failed to update policy");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this policy?")) return;
    try {
      await api.delete(`/insurance/${id}`);
      toast.success("Policy deleted");
      fetchPolicies();
    } catch (error) {
      toast.error("Failed to delete policy");
    }
  };

  const handleAiLookup = async () => {
    if (!selectedPolicy) {
      toast.error("Please select a policy");
      return;
    }
    setAiLoading(true);
    try {
      const res = await api.post("/ai/insurance-lookup", null, {
        params: { 
          policy_number: selectedPolicy.policy_number,
          provider: selectedPolicy.provider
        }
      });
      setAiResult(res.data.analysis);
    } catch (error) {
      toast.error("AI lookup failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiSearch = async () => {
    setSearchLoading(true);
    try {
      const searchData = {
        search_type: searchForm.search_type,
        coverage_amount: searchForm.coverage_amount ? parseFloat(searchForm.coverage_amount) : null,
        monthly_budget: searchForm.monthly_budget ? parseFloat(searchForm.monthly_budget) : null,
        age: searchForm.age ? parseInt(searchForm.age) : null,
        health_status: searchForm.health_status,
        smoker: searchForm.smoker,
        term_length: searchForm.term_length ? parseInt(searchForm.term_length) : null,
        additional_requirements: searchForm.additional_requirements || null
      };
      
      const res = await api.post("/insurance/ai/search", searchData);
      setSearchResult(res.data);
      toast.success("Search complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Search failed");
    } finally {
      setSearchLoading(false);
    }
  };

  const totalDeathBenefit = policies.reduce((sum, p) => sum + p.death_benefit, 0);
  const totalCashValue = policies.reduce((sum, p) => sum + p.cash_value, 0);
  const totalAnnualPremium = policies.reduce((sum, p) => {
    const multiplier = p.payment_frequency === 'monthly' ? 12 : p.payment_frequency === 'quarterly' ? 4 : 1;
    return sum + (p.premium_amount * multiplier);
  }, 0);

  return (
    <Layout title="Life Insurance">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="policies-count-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Policies</p>
              <p className="text-3xl font-bold text-white font-mono">{policies.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="death-benefit-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Heart className="w-4 h-4 text-red-500" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Death Benefit</p>
              </div>
              <p className="text-2xl font-bold text-teal-500 font-mono">{formatCurrency(totalDeathBenefit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="cash-value-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-primary" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Cash Value</p>
              </div>
              <p className="text-2xl font-bold text-primary font-mono">{formatCurrency(totalCashValue)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="annual-premium-card">
            <CardContent className="p-4">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Annual Premium</p>
              <p className="text-2xl font-bold text-orange-500 font-mono">{formatCurrency(totalAnnualPremium)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                data-testid="add-policy-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Policy
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  Add Life Insurance Policy
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Provider</Label>
                  <Select
                    value={formData.provider}
                    onValueChange={(value) => setFormData({ ...formData, provider: value })}
                    required
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="policy-provider-select">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {PROVIDERS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Policy Number</Label>
                  <Input
                    value={formData.policy_number}
                    onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                    placeholder="e.g., POL-123456"
                    required
                    data-testid="policy-number-input"
                    className="bg-black/50 border-white/10 rounded-sm font-mono"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Premium Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.premium_amount}
                      onChange={(e) => setFormData({ ...formData, premium_amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="policy-premium-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Payment Frequency</Label>
                    <Select
                      value={formData.payment_frequency}
                      onValueChange={(value) => setFormData({ ...formData, payment_frequency: value })}
                      required
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="policy-frequency-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annually">Annually</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Death Benefit</Label>
                    <Input
                      type="number"
                      value={formData.death_benefit}
                      onChange={(e) => setFormData({ ...formData, death_benefit: e.target.value })}
                      placeholder="0"
                      required
                      data-testid="policy-death-benefit-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Cash Value</Label>
                    <Input
                      type="number"
                      value={formData.cash_value}
                      onChange={(e) => setFormData({ ...formData, cash_value: e.target.value })}
                      placeholder="0"
                      data-testid="policy-cash-value-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    data-testid="policy-start-date-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                  data-testid="submit-policy-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add Policy
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Policy Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  Edit Insurance Policy
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdatePolicy} className="space-y-4">
                <div className="space-y-2">
                  <Label>Policy Number</Label>
                  <Input
                    value={formData.policy_number}
                    onChange={(e) => setFormData({ ...formData, policy_number: e.target.value })}
                    required
                    className="bg-black/50 border-white/10"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select
                    value={formData.provider}
                    onValueChange={(v) => setFormData({ ...formData, provider: v })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROVIDERS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Premium Amount ($)</Label>
                    <Input
                      type="number"
                      value={formData.premium_amount}
                      onChange={(e) => setFormData({ ...formData, premium_amount: e.target.value })}
                      required
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select
                      value={formData.payment_frequency}
                      onValueChange={(v) => setFormData({ ...formData, payment_frequency: v })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annually">Annually</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Death Benefit ($)</Label>
                    <Input
                      type="number"
                      value={formData.death_benefit}
                      onChange={(e) => setFormData({ ...formData, death_benefit: e.target.value })}
                      required
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Cash Value ($)</Label>
                    <Input
                      type="number"
                      value={formData.cash_value}
                      onChange={(e) => setFormData({ ...formData, cash_value: e.target.value })}
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="bg-black/50 border-white/10"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-primary hover:bg-primary/90 rounded-sm"
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Update Policy
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-sm"
                data-testid="ai-policy-lookup-btn"
              >
                <Bot className="w-4 h-4 mr-2" />
                AI Policy Lookup
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-violet-500/30 rounded-none max-w-2xl ai-active">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Sparkles className="w-5 h-5 text-violet-500" />
                  AI Policy Analysis
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Select Policy</Label>
                  <Select
                    value={selectedPolicy?.id || ""}
                    onValueChange={(value) => setSelectedPolicy(policies.find(p => p.id === value))}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="ai-policy-select">
                      <SelectValue placeholder="Select a policy" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      {policies.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.provider} - {p.policy_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedPolicy && (
                  <div className="p-4 bg-white/5 border border-white/10">
                    <p className="text-sm text-zinc-400">Provider: <span className="text-white">{selectedPolicy.provider}</span></p>
                    <p className="text-sm text-zinc-400">Policy #: <span className="text-white font-mono">{selectedPolicy.policy_number}</span></p>
                    <p className="text-sm text-zinc-400">Death Benefit: <span className="text-teal-400 font-mono">{formatCurrency(selectedPolicy.death_benefit)}</span></p>
                  </div>
                )}
                <Button
                  onClick={handleAiLookup}
                  disabled={aiLoading || !selectedPolicy}
                  className="w-full bg-violet-500 text-white hover:bg-violet-600 rounded-sm"
                  data-testid="run-ai-lookup-btn"
                >
                  {aiLoading ? "Analyzing..." : "Analyze Policy"}
                </Button>
                {aiResult && (
                  <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-sm max-h-96 overflow-y-auto">
                    <p className="text-xs font-mono uppercase tracking-widest text-violet-400 mb-2">AI Analysis</p>
                    <div className="text-sm text-zinc-300 whitespace-pre-wrap">{aiResult}</div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* AI Insurance Search Bot */}
          <Dialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 rounded-sm"
                data-testid="ai-search-bot-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                AI Policy Search
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-purple-500/30 rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Search className="w-5 h-5 text-purple-400" />
                  AI Life Insurance Search Bot
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-zinc-400">
                  Our AI will analyze your needs and recommend the best life insurance policies for you.
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Policy Type</Label>
                    <Select
                      value={searchForm.search_type}
                      onValueChange={(value) => setSearchForm({ ...searchForm, search_type: value })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="any">Any Type</SelectItem>
                        <SelectItem value="term">Term Life</SelectItem>
                        <SelectItem value="whole">Whole Life</SelectItem>
                        <SelectItem value="universal">Universal Life</SelectItem>
                        <SelectItem value="variable">Variable Life</SelectItem>
                        <SelectItem value="final_expense">Final Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Health Status</Label>
                    <Select
                      value={searchForm.health_status}
                      onValueChange={(value) => setSearchForm({ ...searchForm, health_status: value })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="excellent">Excellent</SelectItem>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="poor">Poor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Coverage Amount ($)</Label>
                    <Input
                      type="number"
                      value={searchForm.coverage_amount}
                      onChange={(e) => setSearchForm({ ...searchForm, coverage_amount: e.target.value })}
                      placeholder="e.g., 500000"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Monthly Budget ($)</Label>
                    <Input
                      type="number"
                      value={searchForm.monthly_budget}
                      onChange={(e) => setSearchForm({ ...searchForm, monthly_budget: e.target.value })}
                      placeholder="e.g., 100"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Your Age</Label>
                    <Input
                      type="number"
                      value={searchForm.age}
                      onChange={(e) => setSearchForm({ ...searchForm, age: e.target.value })}
                      placeholder="e.g., 35"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Term Length (years)</Label>
                    <Select
                      value={searchForm.term_length}
                      onValueChange={(value) => setSearchForm({ ...searchForm, term_length: value })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm">
                        <SelectValue placeholder="For term life only" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="">N/A (Permanent)</SelectItem>
                        <SelectItem value="10">10 Years</SelectItem>
                        <SelectItem value="15">15 Years</SelectItem>
                        <SelectItem value="20">20 Years</SelectItem>
                        <SelectItem value="30">30 Years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={searchForm.smoker}
                        onChange={(e) => setSearchForm({ ...searchForm, smoker: e.target.checked })}
                        className="w-4 h-4 rounded bg-black/50 border-white/20"
                      />
                      <span className="text-zinc-300">Smoker / Tobacco User</span>
                    </label>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Additional Requirements</Label>
                  <Input
                    value={searchForm.additional_requirements}
                    onChange={(e) => setSearchForm({ ...searchForm, additional_requirements: e.target.value })}
                    placeholder="e.g., living benefits, accelerated death benefit, no medical exam"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                
                <Button
                  onClick={handleAiSearch}
                  disabled={searchLoading}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 rounded-sm py-3"
                  data-testid="run-ai-search-btn"
                >
                  {searchLoading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Searching...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Find Best Policies
                    </span>
                  )}
                </Button>
                
                {searchResult && (
                  <div className="space-y-4 mt-4">
                    {/* Existing Coverage Summary */}
                    {searchResult.existing_coverage && (
                      <div className="p-4 bg-teal-500/10 border border-teal-500/30 rounded-sm">
                        <h4 className="text-sm font-bold text-teal-400 mb-2 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Your Current Coverage
                        </h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-zinc-500">Death Benefit</p>
                            <p className="text-white font-mono">{formatCurrency(searchResult.existing_coverage.total_death_benefit)}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Monthly Premiums</p>
                            <p className="text-white font-mono">{formatCurrency(searchResult.existing_coverage.total_premiums)}</p>
                          </div>
                          <div>
                            <p className="text-zinc-500">Policies</p>
                            <p className="text-white font-mono">{searchResult.existing_coverage.policy_count}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* AI Recommendations */}
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-sm">
                      <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        AI Recommendations
                      </h4>
                      <div className="text-sm text-zinc-300 whitespace-pre-wrap max-h-96 overflow-y-auto">
                        {searchResult.recommendations?.raw_response || JSON.stringify(searchResult.recommendations, null, 2)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Policies List */}
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="policies-list">
          <CardHeader>
            <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
              Your Policies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-zinc-500">Loading...</div>
            ) : policies.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No policies yet. Add your first life insurance policy!
              </div>
            ) : (
              <div className="space-y-4">
                {policies.map((policy) => (
                  <div
                    key={policy.id}
                    className="p-4 border border-white/10 hover:border-white/20 transition-colors"
                    data-testid={`policy-item-${policy.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded bg-teal-500/20 flex items-center justify-center">
                          <Shield className="w-6 h-6 text-teal-500" />
                        </div>
                        <div>
                          <p className="font-bold text-white">{policy.provider}</p>
                          <p className="text-sm text-zinc-500 font-mono">{policy.policy_number}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditPolicy(policy)}
                          className="text-zinc-500 hover:text-primary hover:bg-primary/10"
                          data-testid={`edit-policy-${policy.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(policy.id)}
                          className="text-zinc-500 hover:text-red-500 hover:bg-red-500/10"
                          data-testid={`delete-policy-${policy.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-zinc-500 uppercase">Death Benefit</p>
                        <p className="text-lg font-bold text-teal-500 font-mono">{formatCurrency(policy.death_benefit)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase">Cash Value</p>
                        <p className="text-lg font-bold text-primary font-mono">{formatCurrency(policy.cash_value)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase">Premium</p>
                        <p className="text-lg font-bold text-white font-mono">{formatCurrency(policy.premium_amount)}</p>
                        <p className="text-xs text-zinc-500">/{policy.payment_frequency}</p>
                      </div>
                      <div>
                        <p className="text-xs text-zinc-500 uppercase">Start Date</p>
                        <p className="text-sm text-zinc-300">{policy.start_date}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
